import React, { useMemo, useState, useEffect } from 'react';
import { CustomerDTO } from '../domain/models';
import { useToast } from '../components/ToastProvider';
import { Button } from '../components/Button';
import { Tooltip } from '../components/Tooltip';
import { Pagination } from '../components/Pagination';
import { useSorting } from '../hooks/useSorting';
import { SortIcon } from '../components/SortIcon';

export type CustomerManagementProps = {
  customers: CustomerDTO[];
  onSave: (customer: CustomerDTO) => void;
  onDelete: (id?: number) => void;
  nextCustomerNumber?: string;
};

export function CustomerManagement({ customers, onSave, onDelete, nextCustomerNumber }: Readonly<CustomerManagementProps>) {
  const { error } = useToast();
  const [editing, setEditing] = useState<CustomerDTO | null>(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 5;
  const { sortedItems, sort, toggleSort, getSortState, ariaFor } = useSorting<CustomerDTO, 'customerNumber' | 'name' | 'address' | 'location' | 'country' | 'mail'>({
    items: customers,
    accessors: {
      customerNumber: c => c.customerNumber || '',
      name: c => [c.firstname, c.lastname].filter(Boolean).join(' ') || '',
      address: c => [c.street, c.buildingNumber].filter(Boolean).join(' ') || '',
      location: c => [c.postcode, c.city].filter(Boolean).join(' ') || '',
      country: c => c.country || '',
      mail: c => c.mail || ''
    }
  });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedItems;
    return sortedItems.filter(c => [c.customerNumber, c.firstname, c.lastname, c.mail, c.city, c.postcode]
      .filter(Boolean)
      .some(v => String(v).toLowerCase().includes(q)));
  }, [sortedItems, query]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    if (page > totalPages) setPage(totalPages);
  }, [filtered, page]);

  const pageItems = useMemo(() => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [filtered, page]);

  const startNew = () => setEditing({ city: 'Berlin', country: 'DE', customerNumber: nextCustomerNumber || undefined });
  const startEdit = (c: CustomerDTO) => setEditing({ ...c });
  const cancel = () => setEditing(null);
  const save = () => {
    if (!editing) return;
    if (!editing.firstname && !editing.lastname) {
      error('Bitte mindestens Vor- oder Nachname angeben.');
      return;
    }
    onSave(editing);
    setEditing(null);
  };
  const headerClass = (field: 'customerNumber' | 'name' | 'address' | 'location' | 'country' | 'mail') =>
    `p-2 cursor-pointer select-none transition-colors ${sort.field === field ? 'bg-[var(--primary-50)] text-[var(--primary-700)] font-semibold rounded-sm ring-1 ring-[var(--primary-600)]/40' : 'hover:bg-[var(--primary-50)]'}`;

  return (
    <div className="bg-white rounded-lg shadow p-4" data-testid="customer-management">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold">Kundenverwaltung</h2>
        <div className="flex gap-2">
          <input className="w-64 border border-slate-300 rounded px-3 py-2 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" placeholder="Suchen…" value={query} onChange={(e) => setQuery(e.target.value)} />
          <Tooltip content="Neuen Kunden anlegen">
            <Button variant="primary" onClick={startNew} data-testid="new-customer-btn">+ Neuer Kunde</Button>
          </Tooltip>
        </div>
      </div>

      {/* Tabelle */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
          <tr className="text-left text-slate-600 border-b">
            <th className={headerClass('customerNumber')} onClick={() => toggleSort('customerNumber')} data-testid="sort-header-customerNumber" aria-sort={ariaFor('customerNumber')}>
              <span className="flex items-center gap-1">Nr. <SortIcon state={getSortState('customerNumber')} /></span>
            </th>
            <th className={headerClass('name')} onClick={() => toggleSort('name')} data-testid="sort-header-name" aria-sort={ariaFor('name')}>
              <span className="flex items-center gap-1">Name <SortIcon state={getSortState('name')} /></span>
            </th>
            <th className={headerClass('address')} onClick={() => toggleSort('address')} data-testid="sort-header-address" aria-sort={ariaFor('address')}>
              <span className="flex items-center gap-1">Adresse <SortIcon state={getSortState('address')} /></span>
            </th>
            <th className={headerClass('location')} onClick={() => toggleSort('location')} data-testid="sort-header-location" aria-sort={ariaFor('location')}>
              <span className="flex items-center gap-1">Ort <SortIcon state={getSortState('location')} /></span>
            </th>
            <th className={headerClass('country')} onClick={() => toggleSort('country')} data-testid="sort-header-country" aria-sort={ariaFor('country')}>
              <span className="flex items-center gap-1">Land <SortIcon state={getSortState('country')} /></span>
            </th>
            <th className={headerClass('mail')} onClick={() => toggleSort('mail')} data-testid="sort-header-mail" aria-sort={ariaFor('mail')}>
              <span className="flex items-center gap-1">E-Mail <SortIcon state={getSortState('mail')} /></span>
            </th>
            <th className="p-2 w-32"></th>
          </tr>
          </thead>
          <tbody>
          {filtered.length === 0 && (
            <tr>
              <td className="p-3 text-slate-500" colSpan={7}>Keine Kunden vorhanden.</td>
            </tr>
          )}
          {pageItems.map((c) => (
            <tr key={c.id} className="border-b last:border-0" data-testid={`customer-row-${c.id}`}>
              <td className="p-2">{c.customerNumber || '—'}</td>
              <td className="p-2">{[c.firstname, c.lastname].filter(Boolean).join(' ') || '—'}</td>
              <td className="p-2">{[c.street, c.buildingNumber].filter(Boolean).join(' ')}</td>
              <td className="p-2">{[c.postcode, c.city].filter(Boolean).join(' ')}</td>
              <td className="p-2">{c.country}</td>
              <td className="p-2">{c.mail}</td>
              <td className="p-2 text-right">
                <Tooltip content="Kunden bearbeiten">
                  <Button variant="secondary" onClick={() => startEdit(c)} data-testid={`edit-customer-${c.id}`}>Editieren</Button>
                </Tooltip>
                <Tooltip content="Kunden dauerhaft löschen">
                  <Button variant="danger" className="ml-2" onClick={() => onDelete(c.id)} data-testid={`delete-customer-${c.id}`}>Löschen</Button>
                </Tooltip>
              </td>
            </tr>
          ))}
          </tbody>
        </table>
      </div>
      <Pagination total={filtered.length} page={page} pageSize={PAGE_SIZE} onChange={setPage} />

      {/* Editor */}
      {editing && (
        <div className="mt-6 border-t pt-4" data-testid="customer-editor">
          <h3 className="font-semibold mb-3">{editing.id ? 'Kunde bearbeiten' : 'Neuer Kunde'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label htmlFor="customerNumber" className="block text-xs font-medium text-slate-600 mb-1">Kundennummer</label>
              <input id="customerNumber" className="border border-slate-300 rounded px-3 py-2 w-full bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" value={editing.customerNumber || ''} onChange={(e) => setEditing({ ...editing, customerNumber: e.target.value })} />
            </div>
            <div>
              <label htmlFor="firstname" className="block text-xs font-medium text-slate-600 mb-1">Vorname</label>
              <input id="firstname" className="border border-slate-300 rounded px-3 py-2 w-full bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" value={editing.firstname || ''} onChange={(e) => setEditing({ ...editing, firstname: e.target.value })} />
            </div>
            <div>
              <label htmlFor="lastname" className="block text-xs font-medium text-slate-600 mb-1">Nachname</label>
              <input id="lastname" className="border border-slate-300 rounded px-3 py-2 w-full bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" value={editing.lastname || ''} onChange={(e) => setEditing({ ...editing, lastname: e.target.value })} />
            </div>
            <div>
              <label htmlFor="mail" className="block text-xs font-medium text-slate-600 mb-1">E-Mail</label>
              <input id="mail" className="border border-slate-300 rounded px-3 py-2 w-full bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" value={editing.mail || ''} onChange={(e) => setEditing({ ...editing, mail: e.target.value })} />
            </div>
            <div className="md:col-span-3">
              <label htmlFor="street" className="block text-xs font-medium text-slate-600 mb-1">Straße</label>
              <input id="street" className="border border-slate-300 rounded px-3 py-2 w-full bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" value={editing.street || ''} onChange={(e) => setEditing({ ...editing, street: e.target.value })} />
            </div>
            <div>
              <label htmlFor="buildingNumber" className="block text-xs font-medium text-slate-600 mb-1">Nr.</label>
              <input id="buildingNumber" className="border border-slate-300 rounded px-3 py-2 w-full bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" value={editing.buildingNumber || ''} onChange={(e) => setEditing({ ...editing, buildingNumber: e.target.value })} />
            </div>
            <div>
              <label htmlFor="postcode" className="block text-xs font-medium text-slate-600 mb-1">PLZ</label>
              <input id="postcode" className="border border-slate-300 rounded px-3 py-2 w-full bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" value={editing.postcode || ''} onChange={(e) => setEditing({ ...editing, postcode: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="city" className="block text-xs font-medium text-slate-600 mb-1">Stadt</label>
              <input id="city" className="border border-slate-300 rounded px-3 py-2 w-full bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" value={editing.city || ''} onChange={(e) => setEditing({ ...editing, city: e.target.value })} />
            </div>
            <div>
              <label htmlFor="country" className="block text-xs font-medium text-slate-600 mb-1">Land</label>
              <input id="country" className="border border-slate-300 rounded px-3 py-2 w-full bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" value={editing.country || ''} onChange={(e) => setEditing({ ...editing, country: e.target.value })} />
            </div>
            <div>
              <label htmlFor="telephone" className="block text-xs font-medium text-slate-600 mb-1">Telefon</label>
              <input id="telephone" className="border border-slate-300 rounded px-3 py-2 w-full bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" value={editing.telephone || ''} onChange={(e) => setEditing({ ...editing, telephone: e.target.value })} />
            </div>
            <div>
              <label htmlFor="idCardNr" className="block text-xs font-medium text-slate-600 mb-1">Ausweis-Nr.</label>
              <input id="idCardNr" className="border border-slate-300 rounded px-3 py-2 w-full bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" value={editing.idCardNr || ''} onChange={(e) => setEditing({ ...editing, idCardNr: e.target.value })} />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Tooltip content="Änderungen speichern">
              <Button variant="primary" onClick={save} data-testid="save-customer-btn">Speichern</Button>
            </Tooltip>
            <Tooltip content="Bearbeitung abbrechen">
              <Button variant="ghost" onClick={cancel} data-testid="cancel-customer-btn">Abbrechen</Button>
            </Tooltip>
          </div>
        </div>
      )}
    </div>
  );
}
